import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import {
  getPayoutDetailsStatus,
  PAYOUT_DETAILS_INCOMPLETE,
  PAYOUT_DETAILS_MISSING,
} from '../../util/stripeConnect';

import { NamedLink } from '../../components';

import css from './PayoutSetupBanner.module.css';

/**
 * Tells a vendor that they can't be paid yet.
 *
 * Without payout details Stripe has nowhere to send the money, so the order form
 * refuses the sale - the listing still looks published, and the only person who
 * never finds out is the vendor. This is the nudge that closes that gap.
 *
 * It deliberately says nothing until the Connect account has been loaded (status
 * 'unknown'), so it can't flash on screen and then vanish, and it stays quiet for
 * shoppers, who have no payouts to set up.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.currentUser - The current user entity
 * @param {Object} [props.stripeAccount] - state.stripeConnectAccount.stripeAccount
 * @param {boolean} [props.stripeAccountFetched] - Whether the Connect account has been loaded
 * @param {boolean} [props.hasListings] - Whether the user has any listings
 * @returns {JSX.Element|null}
 */
const PayoutSetupBanner = props => {
  const {
    className,
    rootClassName,
    currentUser,
    stripeAccount,
    stripeAccountFetched,
    hasListings = true,
  } = props;

  const status = getPayoutDetailsStatus({ currentUser, stripeAccount, stripeAccountFetched });
  const needsAttention = status === PAYOUT_DETAILS_MISSING || status === PAYOUT_DETAILS_INCOMPLETE;

  // Someone who hasn't listed anything yet isn't losing sales, and the listing
  // wizard asks for payout details on the way to publishing. Nagging them before
  // they've started is just noise.
  if (!needsAttention || !hasListings) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);
  const messageId =
    status === PAYOUT_DETAILS_MISSING
      ? 'PayoutSetupBanner.missingText'
      : 'PayoutSetupBanner.incompleteText';

  return (
    <div className={classes} role="status">
      <div className={css.content}>
        <p className={css.title}>
          <FormattedMessage id="PayoutSetupBanner.title" />
        </p>
        <p className={css.text}>
          <FormattedMessage id={messageId} />
        </p>
      </div>
      <NamedLink className={css.action} name="StripePayoutPage">
        <FormattedMessage id="PayoutSetupBanner.link" />
      </NamedLink>
    </div>
  );
};

export default PayoutSetupBanner;
